import {
  FiatCurrency,
  KycAmlVerificationStatus,
  OAuthProvider,
  PaymentProvider,
} from "@nestcoinco/hyper-api-gateway-api-client";
import UAParser from "ua-parser-js";
import { StatsigEvent } from "@statsig/js-client";
import posthog from "posthog-js";
import { singularSdk } from "singular-sdk";

import { getCurrentUrl } from "./helpers";
import { GameType } from "./constants";
import { PaymentStatuses } from "../jotai/atoms/topup";
import { InitiateTopupTriggers } from "../jotai/atoms/analytics";
import { statsigClient } from "../config/statsig";

type RegisterEventMethod = (
  eventName: string,
  props?: Record<string, any>,
) => void;

export enum DownloadAppOptionProperty {
  GET_APP = "get app",
  LOG_IN = "log in",
  SKIP = "skip",
}

export enum UserStatusProperty {
  LOGGED_IN = "logged in",
  LOGGED_OUT = "logged out",
}

export enum SignupOptionProperty {
  LOG_IN = "log in",
  SIGN_UP = "sign up",
}

export enum SignupMethodProperty {
  APPLE = "Apple",
  GOOGLE = "Google",
  FACEBOOK = "Facebook",
  TWITTER = "Twitter",
  PARTNER = "Partner",
}

export enum ReferralOptionProperty {
  CONTINUE = "continue",
  SKIP = "skip",
}

export enum FindFriendsOptionProperty {
  FIND_FRIENDS = "find friends",
  SKIP = "skip",
}

export enum NotificationOptionProperty {
  ENABLE_NOTIFICATIONS = "enable notifications",
  SKIP = "skip",
}

export enum SignupBonusOptionProperty {
  CLAIM_BONUS = "claim bonus",
  SKIP = "skip",
}

export enum AllowLocationOptionProperty {
  ACCESS_GRANTED = "access granted",
  ACCESS_DENIED = "access denied",
  MODAL_CLOSED = "modal closed",
}

export const SIGNUP_METHODS: Record<OAuthProvider, SignupMethodProperty> = {
  [OAuthProvider.GOOGLE]: SignupMethodProperty.GOOGLE,
  [OAuthProvider.FACEBOOK]: SignupMethodProperty.FACEBOOK,
  [OAuthProvider.APPLE]: SignupMethodProperty.APPLE,
  [OAuthProvider.TWITTER]: SignupMethodProperty.TWITTER,
  [OAuthProvider.PARTNER]: SignupMethodProperty.PARTNER,
};

const WEB_EVENT_SOURCE = "web";
const EVENT_NAMES = {
  DOWNLOAD_APP_OPTIONS: "download_app_options",
  FIRST_ONBOARDING_SCREEN: "first_onboarding_screen",
  SIGNUP_OPTIONS: "signup_options",
  CHOOSE_REWARD_SELECTION: "choose_reward_selection",
  SUCCESSFUL_SIGN_UP: "successful_sign_up",
  FAILED_SIGN_UP: "failed_sign_up",
  REFERRAL_OPTIONS: "referral_options",
  REFERRED_BY: "referred_by",
  CLAIM_PROFILE: "claim_profile",
  START_GAMING: "start_gaming",
  FIND_FRIENDS_OPTIONS: "find_friends_options",
  NOTIFICATION_OPTIONS: "notification_options",
  SIGNUP_BONUS_OPTIONS: "signup_bonus_options",
  SELECT_CURRENCY: "select_currency",
  SELECT_DOB: "select_date_of_birth",
  ALLOW_LOCATION_OPTIONS: "allow_location_options",
  INITIATE_TOP_UP: "initiate_top_up",
  CONTINUE_TOP_UP: "continue_top_up",
  COMPLETE_TOP_UP: "complete_top_up",
  SUCCESSFUL_TOP_UP: "successful_top_up",
  SUCCESSFUL_SIGNIN: "successful_signin",
  INITIATE_GAME: "initiate_game",
  CANCEL_GAME: "cancel_game",
  COMPLETE_GAME: "complete_game",
  SHARE_RESULTS: "share_results",
  GO_HOME: "go_home",
  INITIATE_CASHOUT: "initiate_cashout",
  COMPLETE_CASHOUT: "complete_cashout",
  INITIATE_VERIFICATION: "initiate_verification",
  COMPLETE_VERIFICATION: "complete_verification",
  SHARE_REFERRAL_LINK: "share_referral_link",
  INITIATING_TOPUP_PAGE_OPENED: "initiating_topup_page_opened",
  PAYSTACK_REDIRECT: "paystack_redirect",
} as const;

const parser = new UAParser();

const getGenericEventData = () => ({
  event_source: WEB_EVENT_SOURCE,
  screen_name: document.title,
  page_url: getCurrentUrl(),
  mobile_platform: parser.getOS().name,
});

const eventSpecificCustomProperties: Record<string, string[]> = {
  [EVENT_NAMES.DOWNLOAD_APP_OPTIONS]: ["download_app_option", "user_status"],
  [EVENT_NAMES.SIGNUP_OPTIONS]: ["signup_option"],
  [EVENT_NAMES.CHOOSE_REWARD_SELECTION]: ["hrp_earnings_target"],
  [EVENT_NAMES.SUCCESSFUL_SIGN_UP]: ["signup_method"],
  [EVENT_NAMES.FAILED_SIGN_UP]: ["signup_method", "signup_option"],
  [EVENT_NAMES.REFERRAL_OPTIONS]: ["signup_method", "referral_option"],
  [EVENT_NAMES.REFERRED_BY]: [
    "signup_method",
    "referredBy_username",
    "referredBy",
  ],
  [EVENT_NAMES.FIND_FRIENDS_OPTIONS]: ["find_friends_option"],
  [EVENT_NAMES.NOTIFICATION_OPTIONS]: ["notification_option"],
  [EVENT_NAMES.SIGNUP_BONUS_OPTIONS]: ["signup_bonus_option"],
  [EVENT_NAMES.SELECT_DOB]: ["date_of_birth_selected"],
  [EVENT_NAMES.ALLOW_LOCATION_OPTIONS]: ["allow_location_option"],
  [EVENT_NAMES.INITIATE_TOP_UP]: ["button_name"],
  [EVENT_NAMES.CONTINUE_TOP_UP]: [
    "hpr_amount",
    "bonus_hpr_amount",
    "gem_amount",
    "fiat_amount",
    "payment_option",
  ],
  [EVENT_NAMES.COMPLETE_TOP_UP]: [
    "hpr_amount",
    "bonus_hpr_amount",
    "gem_amount",
    "fiat_amount",
    "payment_option",
    "first_time_top_up",
    "transaction_status",
  ],
  [EVENT_NAMES.SUCCESSFUL_SIGNIN]: ["signin_method"],
  [EVENT_NAMES.INITIATE_GAME]: [
    "entry_fee",
    "game_name",
    "game_mode",
    "secure_slot",
  ],
  [EVENT_NAMES.CANCEL_GAME]: ["entry_fee", "game_name", "game_mode"],
  [EVENT_NAMES.COMPLETE_GAME]: [
    "entry_fee",
    "game_name",
    "game_mode",
    "player_position",
    "hpr_reward",
  ],
  [EVENT_NAMES.SHARE_RESULTS]: [
    "entry_fee",
    "game_name",
    "game_mode",
    "player_position",
  ],
  [EVENT_NAMES.GO_HOME]: [
    "entry_fee",
    "game_name",
    "game_mode",
    "player_position",
  ],
  [EVENT_NAMES.INITIATE_CASHOUT]: ["hpr_wallet_balance"],
  [EVENT_NAMES.COMPLETE_CASHOUT]: [
    "hpr_wallet_balance",
    "hpr_amount",
    "fiat_amount_withdrawn",
    "transaction_status",
  ],
  [EVENT_NAMES.COMPLETE_VERIFICATION]: ["user_kyc_country", "kyc_status"],
  [EVENT_NAMES.INITIATING_TOPUP_PAGE_OPENED]: [
    "payment_link",
    "plan_id",
    "payment_link_user_id",
    "is_special_offer?",
  ],
  [EVENT_NAMES.PAYSTACK_REDIRECT]: [
    "payment_link",
    "plan_id",
    "payment_link_user_id",
    "is_special_offer?",
    "is_retry: boolean",
    "retry_count",
  ],
};

const resetEventCustomProperties = (eventName?: string) => {
  if (!eventName) return;

  const customProperties = eventSpecificCustomProperties[eventName];
  if (!customProperties) return;

  const updatedEventProperties = customProperties.reduce(
    (previousValues, eventProperty) => ({
      ...previousValues,
      [eventProperty]: undefined,
    }),
    {},
  );
  window.dataLayer.push(updatedEventProperties);
};

const registerGtmEvent: RegisterEventMethod = (eventName, props) => {
  window.dataLayer = window.dataLayer || [];
  if (window.dataLayer.length) {
    let precedingCustomEvent;

    for (let i = window.dataLayer.length - 1; i >= 0; i--) {
      const event = window.dataLayer[i];
      if (!event || !event.event) continue;
      if (!event.event?.startsWith("gtm.")) {
        precedingCustomEvent = event.event;
        break;
      }
    }

    resetEventCustomProperties(precedingCustomEvent);
  }

  const { userid, username, email, ...nonPiiProps } = props ?? {};
  window.dataLayer.push({
    event: eventName,
    ...getGenericEventData(),
    ...nonPiiProps,
  });
};

const registerStatsigEvent: RegisterEventMethod = (eventName, props) => {
  const eventData: StatsigEvent = {
    eventName,
    metadata: { ...getGenericEventData(), ...props },
  };
  statsigClient.logEvent(eventData);
};

const registerPostHogEvent: RegisterEventMethod = (eventName, props) => {
  posthog.capture(eventName, {
    ...getGenericEventData(),
    ...props,
  });
};

const registerSingularEvent: RegisterEventMethod = (eventName, props) => {
  if (!singularSdk._isInitialized) return;
  singularSdk.event(eventName, {
    ...getGenericEventData(),
    ...props,
  });
};

export interface UserPIIProperties {
  userid?: string;
  username?: string;
  email?: string;
}

export interface UserNonPIIProperties {
  user_currency?: FiatCurrency | string;
  user_country?: string;
}

export type UserProperties = UserPIIProperties & UserNonPIIProperties;

interface ReferralPIIProperties {
  referredBy_username: string;
  referredBy: string;
}

interface DownloadAppOptionsEventProperties extends UserProperties {
  download_app_option: DownloadAppOptionProperty;
  user_status: UserStatusProperty;
}

export const registerDownloadAppOptionsEvent = (
  properties: DownloadAppOptionsEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.DOWNLOAD_APP_OPTIONS, properties);
  registerStatsigEvent(EVENT_NAMES.DOWNLOAD_APP_OPTIONS, properties);
  registerPostHogEvent(EVENT_NAMES.DOWNLOAD_APP_OPTIONS, properties);
  registerSingularEvent(EVENT_NAMES.DOWNLOAD_APP_OPTIONS, properties);
};

export const registerFirstOnboardingScreenEvent = (
  properties: UserPIIProperties,
) => {
  registerGtmEvent(EVENT_NAMES.FIRST_ONBOARDING_SCREEN, properties);
  registerStatsigEvent(EVENT_NAMES.FIRST_ONBOARDING_SCREEN, properties);
  registerPostHogEvent(EVENT_NAMES.FIRST_ONBOARDING_SCREEN, properties);
  registerSingularEvent(EVENT_NAMES.FIRST_ONBOARDING_SCREEN, properties);
};

interface SignupOptionsEventProperties extends UserPIIProperties {
  signup_option: SignupOptionProperty;
}

export const registerSignupOptionsEvent = (
  properties: SignupOptionsEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.SIGNUP_OPTIONS, properties);
  registerStatsigEvent(EVENT_NAMES.SIGNUP_OPTIONS, properties);
  registerPostHogEvent(EVENT_NAMES.SIGNUP_OPTIONS, properties);
  registerSingularEvent(EVENT_NAMES.SIGNUP_OPTIONS, properties);
};

interface ChooseRewardSelectionEventProperties extends UserPIIProperties {
  hrp_earnings_target: number;
}

export const registerChooseRewardSelectionEvent = (
  properties: ChooseRewardSelectionEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.CHOOSE_REWARD_SELECTION, properties);
  registerStatsigEvent(EVENT_NAMES.CHOOSE_REWARD_SELECTION, properties);
  registerPostHogEvent(EVENT_NAMES.CHOOSE_REWARD_SELECTION, properties);
  registerSingularEvent(EVENT_NAMES.CHOOSE_REWARD_SELECTION, properties);
};

interface SuccessfulSignupEventProperties extends UserPIIProperties {
  signup_method: SignupMethodProperty;
}

export const registerSuccessfulSignupEvent = (
  properties: SuccessfulSignupEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.SUCCESSFUL_SIGN_UP, properties);
  registerStatsigEvent(EVENT_NAMES.SUCCESSFUL_SIGN_UP, properties);
  registerPostHogEvent(EVENT_NAMES.SUCCESSFUL_SIGN_UP, properties);
  registerSingularEvent(EVENT_NAMES.SUCCESSFUL_SIGN_UP, properties);
};

export interface FailedSignupEventProperties extends UserPIIProperties {
  signup_method: SignupMethodProperty;
  signup_option: SignupOptionProperty;
}

export const registerFailedSignupEvent = (
  properties: FailedSignupEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.FAILED_SIGN_UP, properties);
  registerStatsigEvent(EVENT_NAMES.FAILED_SIGN_UP, properties);
  registerPostHogEvent(EVENT_NAMES.FAILED_SIGN_UP, properties);
  registerSingularEvent(EVENT_NAMES.FAILED_SIGN_UP, properties);
};

export interface ReferralOptionsEventProperties
  extends UserPIIProperties, Partial<ReferralPIIProperties> {
  signup_method: SignupMethodProperty;
  referral_option: ReferralOptionProperty;
}

export const registerReferralOptionsEvent = (
  properties: ReferralOptionsEventProperties,
) => {
  const { referredBy, referredBy_username, ...nonPiiReferralProperties } =
    properties;
  registerGtmEvent(EVENT_NAMES.REFERRAL_OPTIONS, nonPiiReferralProperties);
  registerStatsigEvent(EVENT_NAMES.REFERRAL_OPTIONS, properties);
  registerPostHogEvent(EVENT_NAMES.REFERRAL_OPTIONS, properties);
  registerSingularEvent(EVENT_NAMES.REFERRAL_OPTIONS, properties);
};

export interface ReferredByEventProperties
  extends UserPIIProperties, ReferralPIIProperties {
  signup_method: SignupMethodProperty;
}

export const registerReferredByEvent = (
  properties: ReferredByEventProperties,
) => {
  const { referredBy, referredBy_username, ...nonPiiReferralProperties } =
    properties;
  registerGtmEvent(EVENT_NAMES.REFERRED_BY, nonPiiReferralProperties);
  registerStatsigEvent(EVENT_NAMES.REFERRED_BY, properties);
  registerPostHogEvent(EVENT_NAMES.REFERRED_BY, properties);
  registerSingularEvent(EVENT_NAMES.REFERRED_BY, properties);
};

export const registerClaimProfileEvent = (properties: UserPIIProperties) => {
  registerGtmEvent(EVENT_NAMES.CLAIM_PROFILE, properties);
  registerStatsigEvent(EVENT_NAMES.CLAIM_PROFILE, properties);
  registerPostHogEvent(EVENT_NAMES.CLAIM_PROFILE, properties);
  registerSingularEvent(EVENT_NAMES.CLAIM_PROFILE, properties);
};

export const registerStartGamingEvent = (properties: UserPIIProperties) => {
  registerGtmEvent(EVENT_NAMES.START_GAMING, properties);
  registerStatsigEvent(EVENT_NAMES.START_GAMING, properties);
  registerPostHogEvent(EVENT_NAMES.START_GAMING, properties);
  registerSingularEvent(EVENT_NAMES.START_GAMING, properties);
};

interface FindFriendOptionsEventProperties extends UserPIIProperties {
  find_friends_option: FindFriendsOptionProperty;
}

export const registerFindFriendOptionsEvent = (
  properties: FindFriendOptionsEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.FIND_FRIENDS_OPTIONS, properties);
  registerStatsigEvent(EVENT_NAMES.FIND_FRIENDS_OPTIONS, properties);
  registerPostHogEvent(EVENT_NAMES.FIND_FRIENDS_OPTIONS, properties);
  registerSingularEvent(EVENT_NAMES.FIND_FRIENDS_OPTIONS, properties);
};

interface NotificationOptionsEventProperties extends UserPIIProperties {
  notification_option: NotificationOptionProperty;
}

export const registerNotificationOptionsEvent = (
  properties: NotificationOptionsEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.NOTIFICATION_OPTIONS, properties);
  registerStatsigEvent(EVENT_NAMES.NOTIFICATION_OPTIONS, properties);
  registerPostHogEvent(EVENT_NAMES.NOTIFICATION_OPTIONS, properties);
  registerSingularEvent(EVENT_NAMES.NOTIFICATION_OPTIONS, properties);
};

interface SignupBonusOptionsEventProperties extends UserPIIProperties {
  signup_bonus_option: SignupBonusOptionProperty;
}

export const registerSignupBonusOptionsEvent = (
  properties: SignupBonusOptionsEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.SIGNUP_BONUS_OPTIONS, properties);
  registerStatsigEvent(EVENT_NAMES.SIGNUP_BONUS_OPTIONS, properties);
  registerPostHogEvent(EVENT_NAMES.SIGNUP_BONUS_OPTIONS, properties);
  registerSingularEvent(EVENT_NAMES.SIGNUP_BONUS_OPTIONS, properties);
};

export const registerSelectCurrencyEvent = (
  properties: Omit<UserProperties, "user_country">,
) => {
  registerGtmEvent(EVENT_NAMES.SELECT_CURRENCY, properties);
  registerStatsigEvent(EVENT_NAMES.SELECT_CURRENCY, properties);
  registerPostHogEvent(EVENT_NAMES.SELECT_CURRENCY, properties);
  registerSingularEvent(EVENT_NAMES.SELECT_CURRENCY, properties);
};

export interface SelectDobEventProperties extends Omit<
  UserProperties,
  "user_country"
> {
  date_of_birth_selected: string;
}

export const registerSelectDobEvent = (
  properties: SelectDobEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.SELECT_DOB, properties);
  registerStatsigEvent(EVENT_NAMES.SELECT_DOB, properties);
  registerPostHogEvent(EVENT_NAMES.SELECT_DOB, properties);
  registerSingularEvent(EVENT_NAMES.SELECT_DOB, properties);
};

interface AllowLocationOptionsEventProperties extends UserProperties {
  allow_location_option: AllowLocationOptionProperty;
}

export const registerAllowLocationOptionsEvent = (
  properties: AllowLocationOptionsEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.ALLOW_LOCATION_OPTIONS, properties);
  registerStatsigEvent(EVENT_NAMES.ALLOW_LOCATION_OPTIONS, properties);
  registerPostHogEvent(EVENT_NAMES.ALLOW_LOCATION_OPTIONS, properties);
  registerSingularEvent(EVENT_NAMES.ALLOW_LOCATION_OPTIONS, properties);
};

export interface InitiateTopupEventProperties extends UserProperties {
  button_name?: InitiateTopupTriggers;
}

export const registerInitiateTopupEvent = (
  properties: InitiateTopupEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.INITIATE_TOP_UP, properties);
  registerStatsigEvent(EVENT_NAMES.INITIATE_TOP_UP, properties);
  registerPostHogEvent(EVENT_NAMES.INITIATE_TOP_UP, properties);
  registerSingularEvent(EVENT_NAMES.INITIATE_TOP_UP, properties);
};

export interface ContinueTopupEventProperties extends UserProperties {
  hpr_amount: number;
  bonus_hpr_amount: number;
  gem_amount: number;
  fiat_amount: number;
  payment_option: PaymentProvider | string;
}

export const registerContinueTopupEvent = (
  properties: ContinueTopupEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.CONTINUE_TOP_UP, properties);
  registerStatsigEvent(EVENT_NAMES.CONTINUE_TOP_UP, properties);
  registerPostHogEvent(EVENT_NAMES.CONTINUE_TOP_UP, properties);
  registerSingularEvent(EVENT_NAMES.CONTINUE_TOP_UP, properties);
};

export interface CompleteTopupEventProperties extends ContinueTopupEventProperties {
  first_time_top_up: boolean;
  transaction_status: PaymentStatuses;
  topUpUniqueId?: string;
}

export const registerCompleteTopupEvent = (
  properties: CompleteTopupEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.COMPLETE_TOP_UP, properties);
  registerStatsigEvent(EVENT_NAMES.COMPLETE_TOP_UP, properties);
  registerPostHogEvent(EVENT_NAMES.COMPLETE_TOP_UP, properties);
  registerSingularEvent(EVENT_NAMES.COMPLETE_TOP_UP, properties);
};

export const registerSuccessfulTopupEvent = (
  properties: CompleteTopupEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.SUCCESSFUL_TOP_UP, properties);
  registerStatsigEvent(EVENT_NAMES.SUCCESSFUL_TOP_UP, properties);
  registerPostHogEvent(EVENT_NAMES.SUCCESSFUL_TOP_UP, properties);
  registerSingularEvent(EVENT_NAMES.SUCCESSFUL_TOP_UP, properties);
};

interface SuccessfulSigninEventProperties extends UserPIIProperties {
  signin_method: SignupMethodProperty;
}

export const registerSuccessfulSigninEvent = (
  properties: SuccessfulSigninEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.SUCCESSFUL_SIGNIN, properties);
  registerStatsigEvent(EVENT_NAMES.SUCCESSFUL_SIGNIN, properties);
  registerPostHogEvent(EVENT_NAMES.SUCCESSFUL_SIGNIN, properties);
  registerSingularEvent(EVENT_NAMES.SUCCESSFUL_SIGNIN, properties);
};

export interface GenericGameEventProperties extends UserProperties {
  entry_fee: number;
  game_name: string;
  game_mode: GameType;
}

export interface InitiateGameEventProperties extends GenericGameEventProperties {
  secure_slot?: boolean;
}

export const registerInitiateGameEvent = (
  properties: InitiateGameEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.INITIATE_GAME, properties);
  registerStatsigEvent(EVENT_NAMES.INITIATE_GAME, properties);
  registerPostHogEvent(EVENT_NAMES.INITIATE_GAME, properties);
  registerSingularEvent(EVENT_NAMES.INITIATE_GAME, properties);
};

export const registerCancelGameEvent = (
  properties: GenericGameEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.CANCEL_GAME, properties);
  registerStatsigEvent(EVENT_NAMES.CANCEL_GAME, properties);
  registerPostHogEvent(EVENT_NAMES.CANCEL_GAME, properties);
  registerSingularEvent(EVENT_NAMES.CANCEL_GAME, properties);
};

export interface CompleteGameEventProperties extends GenericGameEventProperties {
  player_position: number | null;
  hpr_reward?: number;
}

export const registerCompleteGameEvent = (
  properties: CompleteGameEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.COMPLETE_GAME, properties);
  registerStatsigEvent(EVENT_NAMES.COMPLETE_GAME, properties);
  registerPostHogEvent(EVENT_NAMES.COMPLETE_GAME, properties);
  registerSingularEvent(EVENT_NAMES.COMPLETE_GAME, properties);
};

export const registerShareResultsEvent = (
  properties: Omit<CompleteGameEventProperties, "hpr_reward">,
) => {
  registerGtmEvent(EVENT_NAMES.SHARE_RESULTS, properties);
  registerStatsigEvent(EVENT_NAMES.SHARE_RESULTS, properties);
  registerPostHogEvent(EVENT_NAMES.SHARE_RESULTS, properties);
  registerSingularEvent(EVENT_NAMES.SHARE_RESULTS, properties);
};

export const registerGoHomeEvent = (
  properties: Omit<CompleteGameEventProperties, "hpr_reward">,
) => {
  registerGtmEvent(EVENT_NAMES.GO_HOME, properties);
  registerStatsigEvent(EVENT_NAMES.GO_HOME, properties);
  registerPostHogEvent(EVENT_NAMES.GO_HOME, properties);
  registerSingularEvent(EVENT_NAMES.GO_HOME, properties);
};

interface InitiateCashoutEventProperties extends UserProperties {
  hpr_wallet_balance: number;
}

export const registerInitiateCashoutEvent = (
  properties: InitiateCashoutEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.INITIATE_CASHOUT, properties);
  registerStatsigEvent(EVENT_NAMES.INITIATE_CASHOUT, properties);
  registerPostHogEvent(EVENT_NAMES.INITIATE_CASHOUT, properties);
  registerSingularEvent(EVENT_NAMES.INITIATE_CASHOUT, properties);
};

export interface CompleteCashoutEventProperties extends InitiateCashoutEventProperties {
  hpr_amount: number;
  fiat_amount_withdrawn: number;
  transaction_status: PaymentStatuses;
}

export const registerCompleteCashoutEvent = (
  properties: CompleteCashoutEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.COMPLETE_CASHOUT, properties);
  registerStatsigEvent(EVENT_NAMES.COMPLETE_CASHOUT, properties);
  registerPostHogEvent(EVENT_NAMES.COMPLETE_CASHOUT, properties);
  registerSingularEvent(EVENT_NAMES.COMPLETE_CASHOUT, properties);
};

export const registerInitiateVerificationEvent = (
  properties: Omit<UserProperties, "user_currency">,
) => {
  registerGtmEvent(EVENT_NAMES.INITIATE_VERIFICATION, properties);
  registerStatsigEvent(EVENT_NAMES.INITIATE_VERIFICATION, properties);
  registerPostHogEvent(EVENT_NAMES.INITIATE_VERIFICATION, properties);
  registerSingularEvent(EVENT_NAMES.INITIATE_VERIFICATION, properties);
};

export interface CompleteVerificationEventProperties extends Omit<
  UserProperties,
  "user_currency"
> {
  user_kyc_country: string;
  kyc_status: Exclude<
    KycAmlVerificationStatus,
    | KycAmlVerificationStatus.AWAITING_INPUT
    | KycAmlVerificationStatus.UNVERIFIED
  >;
}

export const registerCompleteVerificationEvent = (
  properties: CompleteVerificationEventProperties,
) => {
  registerGtmEvent(EVENT_NAMES.COMPLETE_VERIFICATION, properties);
  registerStatsigEvent(EVENT_NAMES.COMPLETE_VERIFICATION, properties);
  registerPostHogEvent(EVENT_NAMES.COMPLETE_VERIFICATION, properties);
  registerSingularEvent(EVENT_NAMES.COMPLETE_VERIFICATION, properties);
};

export const registerShareReferralLinkEvent = (properties: UserProperties) => {
  registerGtmEvent(EVENT_NAMES.SHARE_REFERRAL_LINK, properties);
  registerStatsigEvent(EVENT_NAMES.SHARE_REFERRAL_LINK, properties);
  registerPostHogEvent(EVENT_NAMES.SHARE_REFERRAL_LINK, properties);
  registerSingularEvent(EVENT_NAMES.SHARE_REFERRAL_LINK, properties);
};

export interface InitiatingTopupPageOpenedEventProperties extends UserPIIProperties {
  payment_link: string;
  plan_id: string | null;
  payment_link_user_id: string | null;
  is_special_offer?: boolean;
}

export const registerInitiatingTopupPageOpenedEvent = (
  properties: InitiatingTopupPageOpenedEventProperties,
) => {
  registerStatsigEvent(EVENT_NAMES.INITIATING_TOPUP_PAGE_OPENED, properties);
};

export interface PaystackRedirectEventProperties extends InitiatingTopupPageOpenedEventProperties {
  is_retry: boolean;
  retry_count: number;
}

export const registerPaystackRedirectEvent = (
  properties: PaystackRedirectEventProperties,
) => {
  registerStatsigEvent(EVENT_NAMES.PAYSTACK_REDIRECT, properties);
};
